import { useState } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export type AvatarSize = "xs" | "sm" | "md" | "lg" | "xl";
export type AvatarShape = "circle" | "square";

export interface AvatarInitialsProps {
  name: string;
  imageUrl?: string;
  size?: AvatarSize;
  shape?: AvatarShape;
  /** Override auto-generated background color */
  color?: string;
  className?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  { bg: "bg-violet-200 dark:bg-violet-800",   text: "text-violet-900 dark:text-violet-100" },
  { bg: "bg-blue-200 dark:bg-blue-800",       text: "text-blue-900 dark:text-blue-100" },
  { bg: "bg-teal-200 dark:bg-teal-800",       text: "text-teal-900 dark:text-teal-100" },
  { bg: "bg-green-200 dark:bg-green-800",     text: "text-green-900 dark:text-green-100" },
  { bg: "bg-amber-200 dark:bg-amber-800",     text: "text-amber-900 dark:text-amber-100" },
  { bg: "bg-orange-200 dark:bg-orange-800",   text: "text-orange-900 dark:text-orange-100" },
  { bg: "bg-rose-200 dark:bg-rose-800",       text: "text-rose-900 dark:text-rose-100" },
  { bg: "bg-indigo-200 dark:bg-indigo-800",   text: "text-indigo-900 dark:text-indigo-100" },
];

function hashName(name: string): number {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  return hash % AVATAR_COLORS.length;
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// ── Size config ───────────────────────────────────────────────────────────────

const SIZE_CONFIG: Record<AvatarSize, { container: string; text: string }> = {
  xs: { container: "w-6 h-6",   text: "text-[10px] font-semibold" },
  sm: { container: "w-8 h-8",   text: "text-xs font-semibold" },
  md: { container: "w-10 h-10", text: "text-sm font-semibold" },
  lg: { container: "w-12 h-12", text: "text-base font-semibold" },
  xl: { container: "w-16 h-16", text: "text-lg font-bold" },
};

// ── Component ─────────────────────────────────────────────────────────────────

export function AvatarInitials({
  name,
  imageUrl,
  size = "md",
  shape = "circle",
  color,
  className,
}: AvatarInitialsProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = !!imageUrl && !imgError;

  const colorIdx = hashName(name);
  const autoColor = AVATAR_COLORS[colorIdx];
  const sizeConfig = SIZE_CONFIG[size];
  const radiusClass = shape === "circle" ? "rounded-full" : "rounded-lg";

  return (
    <span
      className={cn(
        "inline-flex items-center justify-center overflow-hidden shrink-0 select-none",
        sizeConfig.container,
        radiusClass,
        !showImage && (color ? "" : cn(autoColor.bg, autoColor.text)),
        className,
      )}
      style={!showImage && color ? { backgroundColor: color } : undefined}
      aria-label={name}
      title={name}
    >
      {showImage ? (
        <img
          src={imageUrl}
          alt={name}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
        />
      ) : (
        <span className={cn(sizeConfig.text, "leading-none")}>
          {getInitials(name)}
        </span>
      )}
    </span>
  );
}

AvatarInitials.displayName = "AvatarInitials";
